import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './banner.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { animateTo } from 'elements/_shared/animations.js'


initializeIconsByRole({
  'cse-banner.dismiss': { name: 'xmark', style: 'regular' },
})

export const bannerVariants = ['primary', 'secondary', 'error', 'info', 'success', 'warning'] as const
export type bannerVariantTypes = typeof bannerVariants

@customElement('cse-banner')
export class CSEBanner extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: String })
  variant: bannerVariantTypes[number] = 'primary'

  @property({ type: Boolean })
  dismissable = false

  @query('.cse-banner')
  banner!: HTMLDivElement

  private closeAnimation: any
  animationComplete: Promise<void> = Promise.resolve()

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties)

    this.closeAnimation = {
      keyframes: [
        { opacity: 1, scale: 1 },
        { opacity: 0, scale: 0 }
      ],
      options: { duration: 250, easing: 'ease-in-out' }
    }
  }

  override async getUpdateComplete() {
    const update = await super.getUpdateComplete()
    await this.animationComplete
    return update
  }

  private close = async () => {
    let resolve!: () => void
    this.animationComplete = new Promise((res) => { resolve = res })

    await animateTo(
      this.banner,
      this.closeAnimation.keyframes,
      this.closeAnimation.options
    ).then(() => resolve())
    this.style.display = 'none'

    this.dispatchEvent(new CSEBannerDismissedEvent(this))
  }

  override render() {
    return html`
      <div class="${classMap({
        'cse-banner': true,
        [`variant-${this.variant}`]: true,
      })}">
        <div class="content">
          <div class="inner">
            <slot name="icon"></slot>
            <slot class="text-content font-body"></slot>
          </div>
          ${this.dismissable ? html`
            <cse-icon-button
              icon-role="cse-banner.dismiss"
              variant="ghost"
              size="small"
              @click="${this.close}"
              aria-label="${t('dismiss_banner', { ns: 'cse-banner', default: 'Dismiss banner' })}"
            ></cse-icon-button>
          ` : nothing }
        </div>
      </div>
    `
  }
}


export class CSEBannerDismissedEvent extends Event {
  static type = 'cse-banner-dismissed' as const

  public banner: CSEBanner

  constructor(banner: CSEBanner) {
    super(CSEBannerDismissedEvent.type, { bubbles: true, composed: true })

    this.banner = banner
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'cse-banner': CSEBanner
  }
  interface GlobalEventHandlersEventMap {
    [CSEBannerDismissedEvent.type]: CSEBannerDismissedEvent,
  }
}
